import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Subject, filter, take } from 'rxjs';

import { TwcPopupService } from '@topcon/twc-angular';

import { JobSiteService, ProjectHelperService, ToastService, TranslationService } from '@mirage-projects/cw-commons';
import {
    BrowserView,
    CatalogObject,
    CatalogObjectSelection,
    CatalogObjectTypes,
    JobSite,
    JobSiteDetailMenuOptions,
    JobSiteProject,
    JobSiteReferenceData,
    ObjectBrowserSelectionTexts,
    ObjectMenuOption,
    Project,
    ProjectType,
    ResultPage,
    SubMenuOption,
} from '@mirage-projects/cw-models';

import { GridColumn } from '../../models';
import { NavigationButton } from '../../models/navigation-button';
import { PaginationData } from '../../models/pagination-data';
import { AddDataComponent } from '../add-data/add-data.component';
import { ObjectBrowserFilterService } from '../object-browser/filter/filter.service';
import { MainButton } from '../object-browser/object-browser.component';
import { ObjectBrowserSelectionService } from '../object-browser/selection.service';
import { JobSiteDetailSelection } from './job-site-detail-selection';
import { JobSiteDetailService } from './job-site-detail.service';

/**
 * Component that shows the detail of a job site element
 */
@Component({
    selector: 'cw-job-site-detail',
    templateUrl: './job-site-detail.component.html',
    styleUrls: ['./job-site-detail.component.scss'],
    standalone: false,
})
export class JobSiteDetailComponent implements OnInit, AfterViewInit {
    /** The list of secondary options */
    @Input() secondaryMenuOptions: SubMenuOption[] = [];
    /** Sets if the component is loading to show the loading spinner */
    @Input() isLoading = false;

    /** Output to open the project viewer after created */
    @Output() openProjectViewer = new EventEmitter<Project>();
    /** Output to return the option click on secondary menu option */
    @Output() menuOptionClick = new EventEmitter<ObjectMenuOption>();
    /**Output to refresh the list */
    @Output() reloadList = new EventEmitter<void>();
    /** Emits when delete projects button is clicked, passing the list of projects to delete */
    @Output() delete = new EventEmitter<CatalogObject[]>();
    /** Emits when clicks on goToViewer button */
    @Output() goToViewerClick = new EventEmitter<void>();

    columnDataList: GridColumn[] = [];
    mainButton!: MainButton;
    pagination: PaginationData = { currentPage: 1, pageSize: 12, totalElements: 0 };
    browserViews!: BrowserView[];
    data: ResultPage<CatalogObjectSelection> = {} as ResultPage<CatalogObjectSelection>;
    footerSelectionResources: ObjectBrowserSelectionTexts = {
        clearButtonText: 'JOB_SITE.DETAIL.CLEAR',
        deleteButtonText: 'JOB_SITE.DETAIL.DELETE',
        selectedElementsText: 'JOB_SITE.DETAIL.SHOW_SELECTED',
        showSelectedText: 'JOB_SITE.DETAIL.SELECTED',
        showSelectedsText: 'JOB_SITE.DETAIL.SELECTEDS',
    };
    reloadPage = new Subject<number>();
    dataType: CatalogObjectTypes = 'projects';
    goToViewerButton!: NavigationButton;
    showSelectedElements = false;

    private projectsFilters: Map<string, string> | null = null;
    private _jobSiteDetail!: JobSite;

    constructor(
        private translationService: TranslationService,
        private popupService: TwcPopupService,
        private jobSiteDetailService: JobSiteDetailService,
        private selectionService: ObjectBrowserSelectionService,
        private filterService: ObjectBrowserFilterService,
        private projectHelperService: ProjectHelperService,
        private jobSiteService: JobSiteService,
        private toastService: ToastService
    ) {
        this.selectionService.resetSelection();
    }

    ngOnInit(): void {
        this.mainButton = { id: 'ADD_PROJECT_LAYER', label: 'JOB_SITE.VIEWER.TOC_BUTTONS.ADD_PROJECT_LAYER' };
        this.columnDataList = this.getColumnDataList();
        this.browserViews = this.getViewForJobSite();
        this.goToViewerButton = {
            icon: '3d-model',
            tooltip: 'JOB_SITE.DETAIL.VIEWER_BUTTON',
            onClick: () => this.goToViewerClick.emit(),
            qaid: 'button_go-to-site-viewer',
        };

        const filters = this.browserViews[0].filters;
        this.projectsFilters = this.filterService.getFilterParams(filters);
    }

    ngAfterViewInit(): void {
        this.data = this.getProjects(this.pagination.currentPage);
    }

    onShowElementsChanged(haveToShow: boolean): void {
        this.showSelectedElements = haveToShow;
        this.reloadPage.next(1);
    }

    onClearSelection(): void {
        this.clearSelection();
    }

    clearSelection(): void {
        this.showSelectedElements = false;
        this.selectionService.resetSelection();
        this.reloadPage.next(1);
    }

    onDelete(): void {
        this.deleteProject();
    }

    getProjects(currentPage: number): ResultPage<JobSiteDetailSelection> {
        const catalogList: CatalogObjectSelection[] = [];
        const projects = this.jobSiteDetail.projects;
        if (!projects) {
            return { count: 0, next: '', previous: '', results: catalogList };
        }

        let projectsFiltered = this.jobSiteDetailService.filterProjects(projects, this.projectsFilters);

        if (this.showSelectedElements) {
            projectsFiltered = projectsFiltered.filter((item) => this.selectionService.isElementSelected(item, this.dataType));
        }

        const projectsListed = this.jobSiteDetailService
            .orderProjects(projectsFiltered, this.projectsFilters)
            .slice(this.pagination.pageSize * (currentPage - 1), this.pagination.pageSize * currentPage);

        (projectsListed as JobSiteProject[]).forEach((project) => {
            const icon = this.projectHelperService.getIconByProjectType(project.projectType, true);
            project.icon = icon;

            const catalogObjectSelection: JobSiteDetailSelection = {
                catalogObject: project,
                selected: this.selectionService.isElementSelected(project, this.dataType),
                secondaryMenuOptions: this.jobSiteDetailService.getSubMenuOptions(project.projectType),
            };
            catalogList.push(catalogObjectSelection);
        });

        return { count: projectsFiltered.length, next: '', previous: '', results: catalogList };
    }

    getViewForJobSite(): BrowserView[] {
        const filtersGrid = this.jobSiteDetailService.initFilters();

        const browserViews: BrowserView[] = [
            { id: 'list', icon: 'service-menu', tooltip: 'OBJECT_BROWSER.GRID_VIEW', filters: filtersGrid },
        ];
        return browserViews;
    }

    onPageChanged(pageNumber: number): void {
        this.data = this.getProjects(pageNumber);
    }

    onMenuOptionClick(menuOption: ObjectMenuOption): void {
        this.menuOptionClick.emit(menuOption);

        if (menuOption.menuOption.id === JobSiteDetailMenuOptions.DELETE_PROJECT) {
            this.clearSelection();
        }
    }

    onReload(): void {
        this.showSelectedElements = false;
        this.selectionService.resetSelection();
        this.reloadList.emit();
    }

    async deleteProject(): Promise<void> {
        const response = await this.jobSiteDetailService.showDeleteProjectPopup();

        if (response.id !== this.jobSiteDetailService.acceptDeleteButton().id) {
            return;
        }

        try {
            const selectedElements = this.selectionService.getSelectedElements(this.dataType);
            this.delete.emit(selectedElements);
            this.clearSelection();
        } catch {
            this.toastService.error('PROJECTS.ERROR_DELETING_PROJECT');
        }
    }

    openAddProjectPopup(): void {
        const onRefresh$ = new Subject<{ projectId: number; projectType: ProjectType; openViewer: boolean } | null>();
        const onLayerReady$ = new Subject<JobSiteReferenceData>();
        const options = {
            params: {
                jobSite: {
                    id: this.jobSiteDetail.id,
                    horizProjectionEPSG: this.jobSiteDetail.horizProjectionEPSG,
                },
            },
            data: { onRefresh$, onLayerReady$ },
        };
        this.popupService.open(AddDataComponent, options);
        onRefresh$.pipe(take(1), filter(Boolean)).subscribe(({ projectId, projectType, openViewer }) => {
            if (openViewer) {
                this.openProjectViewer.emit({ id: projectId, projectType } as Project);
            } else {
                this.onReload();
            }
        });

        onLayerReady$.pipe(take(1)).subscribe((layerInfo) => {
            this.addReferenceData(layerInfo);
        });
    }

    /**
     * When filter is changed, data has to be refreshed, and the page changes to the first one.
     * @param filter Filter to apply
     */
    onFilterChange(filter: Map<string, string>): void {
        this.projectsFilters = filter;
        this.data = this.getProjects(this.pagination.currentPage);
    }

    /**
     * emits the id and the projectType of the element selected only when the name field is clicked
     * @param event
     */
    onProjectNameClick(event: any): void {
        if (!event) {
            return;
        }

        const { id, projectType } = event.catalogObject;
        this.openProjectViewer.emit({ id, projectType } as Project);
    }

    private getColumnDataList(): GridColumn[] {
        return [
            { id: 'name', name: 'OBJECT_BROWSER.NAME', field: 'catalogObject.name', isColumnVisible: true, type: 'link', width: '45%' },
            {
                id: 'created',
                name: 'OBJECT_BROWSER.CREATED',
                field: 'catalogObject.createdAt',
                isColumnVisible: true,
                type: 'date',
                width: '20%',
            },
            {
                id: 'captured',
                name: 'CREATE_PROJECT.CAPTURE_DATE',
                field: 'catalogObject.captureDate',
                isColumnVisible: true,
                type: 'date',
                dateFormat: 'mediumDate',
                width: '20%',
            },
            { id: 'icon', name: '', field: 'catalogObject.icon', isColumnVisible: true, type: 'icon', width: '8%' },
            { id: 'selected', name: '', field: 'selected', isColumnVisible: true, type: 'checkbox', multiSelection: true, width: '5%' },
            { id: 'secondary', name: '', field: 'secondaryMenuOptions', isColumnVisible: true, type: 'menuOption', width: '1%' },
        ];
    }

    private addReferenceData(layer: JobSiteReferenceData): void {
        if (!this.jobSiteDetail.id) {
            return;
        }

        this.jobSiteService.addReferenceData(this.jobSiteDetail.id, layer).subscribe({
            error: () => this.toastService.error('JOB_SITE.ADD_LAYER.ERROR'),
        });
    }

    /** The data of selected JobSite */
    @Input()
    set jobSiteDetail(value: JobSite) {
        this._jobSiteDetail = value;

        if (value) {
            this.pagination = { currentPage: 1, pageSize: 12, totalElements: value.projects?.length ?? 0 };
            this.data = this.getProjects(this.pagination.currentPage);
        }
    }

    get jobSiteDetail(): JobSite {
        return this._jobSiteDetail;
    }
}
